/**
 * Thin fetch wrapper for the LZ Expense REST API.
 * Always sends credentials (HTTP-only cookie) with every request.
 */

// In dev the Vite proxy forwards /api → http://localhost:8000
// In production set VITE_API_URL to the server's base URL (no trailing slash)
const API_BASE = (import.meta.env.VITE_API_URL as string) || '';

/** Default request timeout in milliseconds */
const REQUEST_TIMEOUT_MS = 8_000;

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    message: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

/**
 * FastAPI error bodies come in two shapes: `{"detail": "some message"}` from
 * HTTPException, or `{"detail": [{"msg": "...", "loc": [...]}]}` from Pydantic
 * validation errors (422). Normalizes both into a single readable string.
 */
function extractErrorMessage(body: any, fallback: string): string {
  const detail = body?.detail ?? body?.message;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail) && detail.length > 0) {
    return detail
      .map((d: any) => (typeof d?.msg === 'string' ? d.msg.replace(/^Value error,\s*/, '') : null))
      .filter(Boolean)
      .join(' ') || fallback;
  }
  return fallback;
}

/** Returns a fetch signal that aborts after `ms` milliseconds */
function timeoutSignal(ms: number): AbortSignal {
  return AbortSignal.timeout(ms);
}

/**
 * Make a JSON API request.
 * Throws ApiError on non-2xx responses.
 * Aborts automatically after REQUEST_TIMEOUT_MS.
 */
export async function apiRequest<T = unknown>(
  path: string,
  options: RequestInit = {},
): Promise<T> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(options.headers as Record<string, string> | undefined),
  };

  let res: Response;
  try {
    res = await fetch(`${API_BASE}${path}`, {
      ...options,
      credentials: 'include',
      headers,
      signal: options.signal ?? timeoutSignal(REQUEST_TIMEOUT_MS),
    });
  } catch (err: any) {
    if (err?.name === 'TimeoutError' || err?.name === 'AbortError') {
      throw new ApiError(0, 'El servidor no responde. Verifica tu conexión.');
    }
    throw err;
  }

  if (res.status === 204) return undefined as T;

  if (!res.ok) {
    let detail = `HTTP ${res.status}`;
    try {
      const body = await res.json();
      detail = extractErrorMessage(body, detail);
    } catch {
      // ignore parse error
    }
    throw new ApiError(res.status, detail);
  }

  return res.json() as Promise<T>;
}

/**
 * Upload a file using multipart/form-data.
 * Do NOT set Content-Type — the browser adds the boundary automatically.
 */
export async function apiUpload<T = unknown>(path: string, formData: FormData, timeoutMs = 30_000): Promise<T> {
  let res: Response;
  try {
    res = await fetch(`${API_BASE}${path}`, {
      method: 'POST',
      credentials: 'include',
      body: formData,
      signal: timeoutSignal(timeoutMs),
    });
  } catch (err: any) {
    if (err?.name === 'TimeoutError' || err?.name === 'AbortError') {
      throw new ApiError(0, 'La carga tardó demasiado. Intenta nuevamente.');
    }
    throw err;
  }

  if (!res.ok) {
    let detail = `HTTP ${res.status}`;
    try {
      const body = await res.json();
      detail = extractErrorMessage(body, detail);
    } catch {
      // ignore
    }
    throw new ApiError(res.status, detail);
  }

  return res.json() as Promise<T>;
}

/**
 * DELETE a resource at the given URL path.
 * Swallows 404 (resource already gone).
 */
export async function apiDelete(path: string): Promise<void> {
  let res: Response;
  try {
    res = await fetch(`${API_BASE}${path}`, {
      method: 'DELETE',
      credentials: 'include',
      signal: timeoutSignal(REQUEST_TIMEOUT_MS),
    });
  } catch (err: any) {
    if (err?.name === 'TimeoutError' || err?.name === 'AbortError') {
      throw new ApiError(0, 'El servidor no responde. Verifica tu conexión.');
    }
    throw err;
  }

  if (!res.ok && res.status !== 404) {
    throw new ApiError(res.status, `HTTP ${res.status}`);
  }
}

